import { defineStore } from 'pinia'
import { ref, reactive } from 'vue'

export const useDownloadStore = defineStore('download', () => {
  const tasks = reactive([]) // [{id, name, progress, status, error, controller, eventSource, speed, expectedTotal}]

  function addTask({ url, name, headers, method = 'GET', body = undefined, expectedTotal = 0, useSSE = false }) {
    const id = Date.now() + Math.random()
    const controller = new AbortController()
    const task = reactive({
      id,
      name,
      progress: 0,
      status: 'downloading', // 'downloading' | 'success' | 'error' | 'cancelled'
      error: '',
      controller,
      eventSource: null,
      speed: '',
      expectedTotal,
      sseTotalSize: 0 // 从SSE获取的总大小
    })
    tasks.push(task)
    if (useSSE) {
      startDownloadTaskWithSSE(task, { url, headers, controller, method, body })
    } else {
      startDownloadTask(task, { url, headers, controller, method, body })
    }
  }

  async function startDownloadTask(task, { url, headers, controller, method = 'GET', body = undefined }) {
    try {
      const response = await fetch(url, { headers, signal: controller.signal, method, body })
      
      // 检查响应状态，如果失败则不创建文件
      if (!response.ok) {
        // 尝试读取错误信息
        let errorMessage = `下载失败: ${response.status} ${response.statusText}`
        try {
          const errorText = await response.text()
          if (errorText) {
            errorMessage = errorText.length > 200 ? `${errorText.substring(0, 200)}...` : errorText
          }
        } catch (e) {
          // 如果读取错误信息失败，使用默认错误信息
        }
        task.status = 'error'
        task.error = errorMessage
        task.speed = ''
        return
      }
      
      // 检查响应类型，如果不是有效的文件类型，也不创建文件
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('text/html')) {
        // 如果返回的是HTML（可能是错误页面），不创建文件
        let errorMessage = '下载失败: 服务器返回了错误页面'
        try {
          const errorText = await response.text()
          if (errorText) {
            // 尝试从HTML中提取错误信息
            const match = errorText.match(/<title>(.*?)<\/title>/i) || errorText.match(/<h1>(.*?)<\/h1>/i)
            if (match && match[1]) {
              errorMessage = `下载失败: ${match[1]}`
            }
          }
        } catch (e) {
          // 如果读取失败，使用默认错误信息
        }
        task.status = 'error'
        task.error = errorMessage
        task.speed = ''
        return
      }
      
      const contentLength = response.headers.get('content-length')
      const total = contentLength ? parseInt(contentLength, 10) : (task.expectedTotal || 0)
      let loaded = 0
      const reader = response.body.getReader()
      const chunks = []
      let lastLoaded = 0
      let lastTime = Date.now()
      task.speed = ''
      // 定时器用于每秒刷新速度
      let speedTimer = setInterval(() => {
        const now = Date.now()
        const delta = loaded - lastLoaded
        const seconds = (now - lastTime) / 1000
        if (seconds > 0) {
          task.speed = formatSpeed(delta / seconds)
        }
        lastLoaded = loaded
        lastTime = now
      }, 1000)
      function formatSpeed(bytesPerSec) {
        if (bytesPerSec > 1024 * 1024) return (bytesPerSec / 1024 / 1024).toFixed(2) + ' MB/s'
        if (bytesPerSec > 1024) return (bytesPerSec / 1024).toFixed(1) + ' KB/s'
        return bytesPerSec.toFixed(0) + ' B/s'
      }
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        chunks.push(value)
        loaded += value.length
        if (total) task.progress = Math.floor((loaded / total) * 100)
      }
      clearInterval(speedTimer)
      task.speed = ''
      if (task.status === 'cancelled') return
      
      const blob = new Blob(chunks)
      
      // 检查Blob大小，如果为0则不创建文件
      if (blob.size === 0) {
        task.status = 'error'
        task.error = '下载失败: 文件大小为0，可能下载失败'
        task.speed = ''
        return
      }
      
      triggerDownload(blob, task.name)
      task.progress = 100
      task.status = 'success'
    } catch (e) {
      task.speed = ''
      if (e.name === 'AbortError') {
        task.status = 'cancelled'
        task.error = '已取消'
      } else {
        task.status = 'error'
        task.error = e.message || '下载失败，请重试'
      }
    }
  }

  function removeTask(id) {
    const idx = tasks.findIndex(t => t.id === id)
    if (idx !== -1) tasks.splice(idx, 1)
  }

  async function startDownloadTaskWithSSE(task, { url, headers, controller, method = 'POST', body = undefined }) {
    let downloadBlob = null // 保存下载的ZIP文件流
    
    try {
      // 首先发送POST请求启动下载任务，响应体是ZIP文件流，响应头包含downloadId
      const initResponse = await fetch(url, {
        headers,
        signal: controller.signal,
        method,
        body
      })

      if (!initResponse.ok) {
        const errorText = await initResponse.text().catch(() => '')
        throw new Error(`请求失败: ${initResponse.status} ${initResponse.statusText}${errorText ? ` - ${errorText}` : ''}`)
      }

      // 从响应头获取downloadId（支持多种字段名）
      const downloadId = initResponse.headers.get('X-Download-Id') || 
                        initResponse.headers.get('Download-Id') ||
                        initResponse.headers.get('x-download-id') ||
                        initResponse.headers.get('download-id')
      
      if (!downloadId) {
        // 获取所有响应头用于调试
        const allHeaders = {}
        initResponse.headers.forEach((value, key) => {
          allHeaders[key] = value
        })
        
        console.error('响应头信息:', {
          'X-Download-Id': initResponse.headers.get('X-Download-Id'),
          'Download-Id': initResponse.headers.get('Download-Id'),
          'x-download-id': initResponse.headers.get('x-download-id'),
          'download-id': initResponse.headers.get('download-id'),
          'all-headers-array': Array.from(initResponse.headers.entries()),
          'all-headers-object': allHeaders,
          'headers-keys': Array.from(initResponse.headers.keys()),
          'accessible-headers': Object.keys(allHeaders).join(', ')
        })
        
        // 尝试从 Content-Disposition 或其他响应头中提取 downloadId
        const contentDisposition = initResponse.headers.get('Content-Disposition')
        console.log('Content-Disposition:', contentDisposition)
        
        // 这是一个 CORS 配置问题
        // 后端需要在响应头中添加 Access-Control-Expose-Headers
        const errorMsg = `无法从响应头获取 downloadId。这是 CORS 配置问题。

问题原因：
后端虽然设置了 download-id 和 x-download-id 响应头，但浏览器默认只暴露以下响应头：
- Cache-Control
- Content-Language
- Content-Type
- Expires
- Last-Modified
- Pragma

解决方案：
后端需要在响应头中添加 Access-Control-Expose-Headers，例如：
  res.setHeader('Access-Control-Expose-Headers', 'X-Download-Id, Download-Id, x-download-id, download-id');

或者在使用 CORS 中间件时：
  app.use(cors({
    exposedHeaders: ['X-Download-Id', 'Download-Id', 'x-download-id', 'download-id']
  }));

当前可访问的响应头: ${Object.keys(allHeaders).join(', ')}`
        
        console.error(errorMsg)
        throw new Error('无法获取 downloadId：后端未在 Access-Control-Expose-Headers 中暴露自定义响应头。请检查后端 CORS 配置。')
      }

      console.log('从响应头获取的downloadId:', downloadId)

      // 构建SSE URL
      // 格式：/api/{resource}/batch-download/progress/{downloadId}?token=xxx
      // 从原始URL中提取基础URL（协议+域名+端口）
      const urlObj = new URL(url)
      const baseUrl = `${urlObj.protocol}//${urlObj.host}`
      
      // 根据原始URL判断是视频还是音频的批量下载
      // 如果URL包含 /api/video/batch-download，则使用视频的SSE路径
      // 否则使用音频的SSE路径（保持向后兼容）
      let resourceType = 'audio' // 默认使用音频
      if (url.includes('/api/video/batch-download')) {
        resourceType = 'video'
      }
      
      let sseUrl = `${baseUrl}/api/${resourceType}/batch-download/progress/${downloadId}`
      
      // EventSource不支持自定义headers，需要通过URL参数传递token
      const token = headers?.Authorization?.replace('Bearer ', '') || ''
      if (token) {
        sseUrl = `${sseUrl}?token=${encodeURIComponent(token)}`
      }
      
      console.log('SSE连接URL:', sseUrl)
      
      // 先建立SSE连接来显示进度，不要阻塞在读取响应体上
      const eventSource = new EventSource(sseUrl)
      task.eventSource = eventSource
      
      // 添加SSE连接状态监听
      eventSource.onopen = () => {
        console.log('SSE连接已建立')
      }
      
      eventSource.onerror = (error) => {
        // readyState: 0=CONNECTING, 1=OPEN, 2=CLOSED
        // 在连接建立过程中（CONNECTING）或连接关闭后（CLOSED）可能会触发error事件
        // 如果连接已经成功建立过（readyState === 1），这些error可以忽略
        if (eventSource.readyState === EventSource.CLOSED) {
          // 连接已关闭，可能是正常完成
          if (task.status === 'downloading') {
            console.warn('SSE连接已关闭，但任务状态仍为downloading')
          } else {
            console.log('SSE连接正常关闭')
          }
        } else if (eventSource.readyState === EventSource.CONNECTING) {
          // 连接建立过程中的错误，可能是网络问题
          console.error('SSE连接建立失败:', error)
        } else {
          console.error('SSE连接错误:', error, 'readyState:', eventSource.readyState)
        }
      }
      
      // 使用 ReadableStream 来跟踪实际下载进度
      // 从响应头获取总大小（如果可用）
      const contentLength = initResponse.headers.get('content-length')
      let localTotal = contentLength ? parseInt(contentLength, 10) : 0
      
      // 初始化本地下载跟踪变量
      let localLoaded = 0
      let localLastLoaded = 0
      let localLastTime = Date.now()
      let speedLastUpdateTime = Date.now() // 速度上次更新时间
      const SPEED_UPDATE_INTERVAL = 2000 // 速度更新间隔：2秒
      
      // 使用 ReadableStream 读取响应体，同时跟踪下载进度
      const reader = initResponse.body.getReader()
      const chunks = []
      
      // 读取流的函数
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read()
            
            if (done) {
              break
            }
            
            chunks.push(value)
            localLoaded += value.length
            
            // 更新本地进度（实时更新）
            const now = Date.now()
            
            // 如果SSE传过来了totalSize，使用它；否则使用Content-Length
            if (task.sseTotalSize && task.sseTotalSize > 0) {
              localTotal = task.sseTotalSize
            }
            
            // 计算进度（基于实际下载字节数，实时更新）
            if (localTotal > 0) {
              task.progress = Math.min(100, Math.max(0, Math.floor((localLoaded / localTotal) * 100)))
            }
            
            // 计算速度（每2秒更新一次）
            const timeSinceLastSpeedUpdate = now - speedLastUpdateTime
            if (timeSinceLastSpeedUpdate >= SPEED_UPDATE_INTERVAL) {
              const delta = localLoaded - localLastLoaded
              const seconds = timeSinceLastSpeedUpdate / 1000
              
              if (seconds > 0 && delta > 0) {
                task.speed = formatSpeed(delta / seconds)
              }
              
              localLastLoaded = localLoaded
              speedLastUpdateTime = now
            }
          }
          
          // 合并所有 chunks 为 Blob
          const blob = new Blob(chunks)
          downloadBlob = blob
          console.log('ZIP文件流已接收，大小:', blob.size, 'bytes')
          return blob
        } catch (error) {
          console.error('读取ZIP文件流失败:', error)
          throw error
        }
      }
      
      // 保存 Promise 引用，以便在完成事件中等待
      task.blobPromise = readStream()

      // 初始化进度跟踪变量
      task.lastLoaded = 0
      task.lastTime = Date.now()
      let total = task.expectedTotal || 0

      // 监听进度事件（只保存totalSize，进度和速度由本地ReadableStream计算）
      eventSource.addEventListener('progress', (event) => {
        console.log('收到SSE进度事件:', event.data)
        try {
          const data = JSON.parse(event.data)
          console.log('解析后的进度数据:', data)
          // 只从SSE获取totalSize，用于计算进度
          if (data.total !== undefined || data.totalSize !== undefined) {
            task.sseTotalSize = data.total || data.totalSize || 0
            console.log('从SSE获取totalSize:', task.sseTotalSize)
          }
          // 进度和速度由本地ReadableStream的读取进度计算，不在这里更新
        } catch (e) {
          console.error('解析进度数据失败:', e, event.data)
        }
      })

      // 监听完成事件
      eventSource.addEventListener('complete', async (event) => {
        console.log('收到SSE完成事件:', event.data)
        try {
          const data = JSON.parse(event.data)
          console.log('解析后的完成数据:', data)
          eventSource.close()
          task.eventSource = null
          
          // 等待ZIP文件流读取完成（如果还在读取中）
          let finalBlob = downloadBlob
          if (!finalBlob || finalBlob.size === 0) {
            // 如果Blob还没有准备好，等待Promise完成
            if (task.blobPromise) {
              try {
                finalBlob = await task.blobPromise
                downloadBlob = finalBlob
              } catch (e) {
                console.error('等待ZIP文件流失败:', e)
              }
            }
          }
          // 处理进度消息（支持 percent 字段）
          else if (data.type === 'progress' || data.progress !== undefined || data.percent !== undefined) {
            const progress = data.progress !== undefined ? data.progress : (data.percent !== undefined ? data.percent : undefined)
            if (progress !== undefined) {
              task.progress = Math.min(100, Math.max(0, progress))
              console.log('更新进度:', task.progress + '%')
            }
            if (data.loaded !== undefined || data.bytesWritten !== undefined) {
              task.lastLoaded = data.loaded || data.bytesWritten || 0
            }
            if (data.total !== undefined || data.totalSize !== undefined) {
              total = data.total || data.totalSize || 0
            }
            
            // 如果有speed字段，直接使用
            if (data.speed !== undefined) {
              task.speed = formatSpeed(data.speed * 1024) // 假设后端返回的是KB/s
            } else {
              // 计算速度
              const now = Date.now()
              const delta = task.lastLoaded - (task.prevLoaded || 0)
              const seconds = (now - task.lastTime) / 1000
              if (seconds > 0 && delta > 0) {
                task.speed = formatSpeed(delta / seconds)
              }
            }
            task.prevLoaded = task.lastLoaded
            task.lastTime = Date.now()
          }
          
          // 使用ZIP文件流触发下载
          if (finalBlob && finalBlob.size > 0) {
            triggerDownload(finalBlob, task.name)
            task.progress = 100
            task.status = 'success'
            task.speed = ''
            downloadBlob = null // 清理引用
          } else {
            // 如果Blob不可用，尝试从完成事件中获取下载URL
            if (data.downloadUrl || data.url) {
              const downloadUrl = (data.downloadUrl || data.url).startsWith('http') 
                ? (data.downloadUrl || data.url)
                : `${url.split('/api')[0]}/api${data.downloadUrl || data.url}`
              
              const fileResponse = await fetch(downloadUrl, {
                headers: {
                  'Authorization': headers?.Authorization || ''
                }
              })
              
              if (!fileResponse.ok) {
                throw new Error(`下载文件失败: ${fileResponse.status}`)
              }
              
              const blob = await fileResponse.blob()
              triggerDownload(blob, task.name)
              task.progress = 100
              task.status = 'success'
              task.speed = ''
            } else {
              throw new Error('下载完成但文件数据不可用')
            }
          }
        } catch (e) {
          console.error('处理完成事件失败:', e)
          task.status = 'error'
          task.error = e.message || '下载完成但处理失败'
          task.speed = ''
        }
      })

      // 监听错误事件（作为addEventListener的补充）
      eventSource.addEventListener('error', (event) => {
        console.error('SSE error事件触发:', event, 'readyState:', eventSource.readyState)
        // EventSource在连接错误时会触发error事件
        // 但我们需要检查readyState来判断是否是真正的错误
        if (eventSource.readyState === EventSource.CLOSED) {
          // 连接已关闭，可能是正常完成或错误
          // 如果状态还是downloading，说明是异常关闭
          if (task.status === 'downloading') {
            console.error('SSE连接异常关闭')
            eventSource.close()
            task.eventSource = null
            task.status = 'error'
            task.error = 'SSE连接异常关闭'
            task.speed = ''
          }
        }
      })

      // 监听通用消息（兼容不同的SSE实现，作为fallback）
      eventSource.onmessage = (event) => {
        console.log('收到SSE通用消息:', event.data, 'event.type:', event.type)
        try {
          const data = JSON.parse(event.data)
          console.log('解析后的消息数据:', data)
          
          // 先检查完成状态（优先级高于进度）
          if (data.type === 'complete' || data.status === 'complete' || data.status === 'completed') {
            console.log('检测到完成状态，准备触发下载')
            eventSource.close()
            task.eventSource = null
            
            // 更新进度为100%
            if (data.percent !== undefined) {
              task.progress = Math.min(100, Math.max(0, data.percent))
            } else {
              task.progress = 100
            }
            
            // 等待ZIP文件流读取完成（如果还在读取中）
            const waitForBlob = async () => {
              let finalBlob = downloadBlob
              if (!finalBlob || finalBlob.size === 0) {
                // 如果Blob还没有准备好，等待Promise完成
                if (task.blobPromise) {
                  try {
                    console.log('等待ZIP文件流Promise完成...')
                    finalBlob = await task.blobPromise
                    downloadBlob = finalBlob
                    console.log('ZIP文件流已准备好，大小:', finalBlob.size, 'bytes')
                  } catch (e) {
                    console.error('等待ZIP文件流失败:', e)
                  }
                }
              }
              
              // 使用ZIP文件流触发下载
              if (finalBlob && finalBlob.size > 0) {
                console.log('触发文件下载，文件名:', task.name, '大小:', finalBlob.size)
                triggerDownload(finalBlob, task.name)
                task.progress = 100
                task.status = 'success'
                task.speed = ''
                downloadBlob = null // 清理引用
              } else if (data.downloadUrl || data.url) {
                // 如果Blob不可用，尝试从完成事件中获取下载URL
                const downloadUrl = (data.downloadUrl || data.url).startsWith('http') 
                  ? (data.downloadUrl || data.url)
                  : `${url.split('/api')[0]}/api${data.downloadUrl || data.url}`
                
                fetch(downloadUrl, {
                  headers: {
                    'Authorization': headers?.Authorization || ''
                  }
                }).then(response => response.blob())
                  .then(blob => {
                    triggerDownload(blob, task.name)
                    task.progress = 100
                    task.status = 'success'
                    task.speed = ''
                  })
                  .catch(e => {
                    console.error('下载文件失败:', e)
                    task.status = 'error'
                    task.error = '下载文件失败'
                    task.speed = ''
                  })
              } else {
                console.error('下载完成但文件数据不可用，Blob:', finalBlob)
                task.status = 'error'
                task.error = '下载完成但文件数据不可用'
                task.speed = ''
              }
            }
            
            waitForBlob().catch(e => {
              console.error('处理完成消息失败:', e)
              task.status = 'error'
              task.error = e.message || '处理完成消息失败'
              task.speed = ''
            })
          } 
          // 处理进度消息（只使用SSE传过来的totalSize，进度和速度用本地计算的）
          else if (data.type === 'progress' || data.progress !== undefined || data.percent !== undefined || data.status === 'processing') {
            // 只从SSE获取totalSize，用于计算进度
            if (data.total !== undefined || data.totalSize !== undefined) {
              task.sseTotalSize = data.total || data.totalSize || 0
              console.log('从SSE获取totalSize:', task.sseTotalSize)
            }
            // 进度和速度由本地ReadableStream的读取进度计算，不在这里更新
          }
          // 处理错误消息
          else if (data.type === 'error' || data.status === 'error') {
            eventSource.close()
            task.eventSource = null
            task.status = 'error'
            task.error = data.message || '下载失败'
            task.speed = ''
          }
        } catch (e) {
          console.error('解析SSE消息失败:', e, event.data)
        }
      }

      // 监听取消信号
      controller.signal.addEventListener('abort', () => {
        if (eventSource) {
          eventSource.close()
          task.eventSource = null
        }
      })

    } catch (e) {
      task.speed = ''
      if (e.name === 'AbortError') {
        task.status = 'cancelled'
        task.error = '已取消'
      } else {
        task.status = 'error'
        task.error = e.message || '启动下载失败'
      }
      if (task.eventSource) {
        task.eventSource.close()
        task.eventSource = null
      }
      // 清理下载的Blob
      if (downloadBlob) {
        downloadBlob = null
      }
    }
  }

  function formatSpeed(bytesPerSec) {
    if (bytesPerSec > 1024 * 1024) return (bytesPerSec / 1024 / 1024).toFixed(2) + ' MB/s'
    if (bytesPerSec > 1024) return (bytesPerSec / 1024).toFixed(1) + ' KB/s'
    return bytesPerSec.toFixed(0) + ' B/s'
  }

  function triggerDownload(blob, filename) {
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  function cancelTask(id) {
    const task = tasks.find(t => t.id === id)
    if (task && task.status === 'downloading') {
      if (task.eventSource) {
        task.eventSource.close()
        task.eventSource = null
      }
      if (task.controller) {
        task.controller.abort()
      }
      task.status = 'cancelled'
      task.error = '已取消'
      task.speed = ''
    }
  }

  function clearAll() {
    tasks.splice(0, tasks.length)
  }

  return { tasks, addTask, removeTask, cancelTask, clearAll }
}) 